import React, { useState, useEffect } from 'react'
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PencilIcon,
  TrashIcon,
  // EyeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowPathIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import { apiService } from '../services/api'

interface RestaurantSettings {
  restaurant_name: string
  phone_number: string
  address: string
  email: string
  website: string
  currency: string
  currency_symbol: string
  currency_display_type: 'text' | 'icon'
  currency_display_position: 'left' | 'right'
  currency_icon_url: string
  timezone: string
  language: string
  tax_rate: number
  service_charge: number
}

interface InventoryItem {
  id: string
  name: string
  category: string
  currentStock: number
  minStock: number
  maxStock: number
  unit: string
  costPerUnit: number
  supplier: string
  lastUpdated: Date
  status: 'in_stock' | 'low_stock' | 'out_of_stock'
  location: string
  location_id?: string | null
  barcode?: string
  description?: string
}

interface StockTransaction {
  id: string
  inventory_id: string
  transaction_type: 'received' | 'issued' | 'adjusted' | 'returned' | 'wastage'
  quantity: number
  unit_price: number
  total_cost: number
  balance_before: number
  balance_after: number
  reference_number?: string
  reference_type?: string
  reference_id?: string
  notes?: string
  supplier_id?: string
  created_by?: string
  created_at: string
  inventory?: {
    id: string
    name: string
    unit: string
    category?: string
  }
  supplier?: {
    id: string
    name: string
  }
  created_by_user?: {
    id: string
    name: string
    email: string
  }
}

const Inventory: React.FC = () => {
  const [inventory, setInventory] = useState<InventoryItem[]>([])
  const [filteredInventory, setFilteredInventory] = useState<InventoryItem[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null)
  const [newItem, setNewItem] = useState<Partial<InventoryItem>>({})
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<InventoryItem | null>(null)
  const [showStockModal, setShowStockModal] = useState(false)
  const [itemToUpdateStock, setItemToUpdateStock] = useState<InventoryItem | null>(null)
  const [stockUpdateType, setStockUpdateType] = useState<'add' | 'remove'>('add')
  const [stockQuantity, setStockQuantity] = useState<number>(0)
  const [locations, setLocations] = useState<any[]>([])
  const [vendors, setVendors] = useState<any[]>([])
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('')
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false)
  const [filteredSuppliers, setFilteredSuppliers] = useState<any[]>([])

  const [inventoryCategories, setInventoryCategories] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState<'items' | 'transactions' | 'ledger' | 'reports'>('items')
  
  // Stock Transaction states
  const [showTransactionModal, setShowTransactionModal] = useState(false)
  const [transactionType, setTransactionType] = useState<'received' | 'issued' | 'adjusted' | 'returned' | 'wastage'>('received')
  const [transactionItem, setTransactionItem] = useState<InventoryItem | null>(null)
  const [transactionQuantity, setTransactionQuantity] = useState<number>(0)
  const [transactionUnitPrice, setTransactionUnitPrice] = useState<number>(0)
  const [transactionReference, setTransactionReference] = useState<string>('')
  const [transactionNotes, setTransactionNotes] = useState<string>('')
  const [transactionSupplier, setTransactionSupplier] = useState<string>('')
  const [isProcessingTransaction, setIsProcessingTransaction] = useState(false)

  // Stock Ledger states
  const [stockTransactions, setStockTransactions] = useState<StockTransaction[]>([])
  const [loadingTransactions, setLoadingTransactions] = useState(false)
  const [ledgerFilters, setLedgerFilters] = useState({
    inventory_id: '',
    transaction_type: '',
    start_date: '',
    end_date: ''
  })

  // Stock Reports states
  const [stockReports, setStockReports] = useState<any>(null)
  const [loadingReports, setLoadingReports] = useState(false)
  const [reportFilters, setReportFilters] = useState({
    inventory_id: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    group_by: 'day' as 'day' | 'week' | 'month'
  })

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const [restaurantSettings, setRestaurantSettings] = useState<RestaurantSettings>({
    restaurant_name: '',
    phone_number: '',
    address: '',
    email: '',
    website: '',
    currency: 'SAR',
    currency_symbol: 'ر.س',
    currency_display_type: 'text',
    currency_display_position: 'left',
    currency_icon_url: '',
    timezone: 'Asia/Riyadh',
    language: 'English',
    tax_rate: 15,
    service_charge: 10
  })

  useEffect(() => {
    loadInventory()
    loadLocations()
    loadVendors()
    loadInventoryCategories()
    loadRestaurantSettings()
  }, [])

  const loadRestaurantSettings = async () => {
    try {
      const response = await apiService.getRestaurantSettings()
      if (response.success && response.data) {
        setRestaurantSettings(response.data as unknown as RestaurantSettings)
      }
    } catch (error) {
      console.error('Error loading restaurant settings:', error)
    }
  }

  const formatCurrency = (amount: number | string | null | undefined): string => {
    // Convert to number if it's a string or handle null/undefined
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : (amount || 0)
    if (isNaN(numAmount)) {
      return '0.00'
    }
    const formattedAmount = numAmount.toFixed(2)
    const symbol = restaurantSettings.currency_symbol || 'SAR'
    
    if (restaurantSettings.currency_display_position === 'left') {
      return `${symbol}${formattedAmount}`
    } else {
      return `${formattedAmount}${symbol}`
    }
  }

  const loadLocations = async () => {
    try {
      const response = await apiService.getInventoryLocations()
      if (response.success && response.data) {
        // Use flat array for dropdown
        setLocations((response as any).flat || (Array.isArray(response.data) ? response.data : []) || [])
      }
    } catch (error) {
      console.error('Error loading locations:', error)
    }
  }

  // Helper function to get location path (for display)
  const getLocationPath = (locationId: string | null): string => {
    if (!locationId) return ''
    const location = locations.find(l => l.id === locationId)
    if (!location) return ''
    if (!location.parent_id) return location.name
    const parent = locations.find(l => l.id === location.parent_id)
    if (parent) {
      return `${parent.name} > ${location.name}`
    }
    return location.name
  }

  const loadVendors = async () => {
    try {
      const response = await apiService.getVendors()
      if (response.success && response.data && Array.isArray(response.data)) {
        // Map vendor_name to name for compatibility
        const mappedVendors = (response.data as any[]).map((vendor: any) => ({
          ...vendor,
          name: vendor.name || vendor.vendor_name,
        }))
        setVendors(mappedVendors)
      }
    } catch (error) {
      console.error('Error loading vendors:', error)
    }
  }

  useEffect(() => {
    filterAndSortInventory()
  }, [inventory, searchTerm, selectedCategory, selectedStatus, sortBy, sortOrder])

  // Filter suppliers based on search term
  useEffect(() => {
    if (supplierSearchTerm.trim() === '') {
      setFilteredSuppliers(vendors)
    } else {
      const filtered = vendors.filter(vendor => {
        const vendorName = (vendor.name || vendor.vendor_name || '').toLowerCase()
        return vendorName.includes(supplierSearchTerm.toLowerCase())
      })
      setFilteredSuppliers(filtered)
    }
  }, [supplierSearchTerm, vendors])

  // Initialize filtered suppliers when vendors are loaded
  useEffect(() => {
    setFilteredSuppliers(vendors)
  }, [vendors])

  const loadInventory = async () => {
    try {
      const response = await apiService.getInventory()
      if (response.success && response.data && Array.isArray(response.data)) {
        // Map dates to Date objects
        const mappedInventory = (response.data as any[]).map((item: any) => ({
          ...item,
          lastUpdated: item.lastUpdated ? new Date(item.lastUpdated) : new Date()
        }))
        setInventory(mappedInventory)
      } else {
        console.error('Error loading inventory:', response.error)
        setInventory([])
      }
    } catch (error) {
      console.error('Error loading inventory:', error)
      setInventory([])
    }
  }

  const filterAndSortInventory = () => {
    let filtered = [...inventory]

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.supplier.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory)
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(item => item.status === selectedStatus)
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: any = a[sortBy as keyof InventoryItem]
      let bValue: any = b[sortBy as keyof InventoryItem]

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
    })

    setFilteredInventory(filtered)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in_stock': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-200'
      case 'low_stock': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200'
      case 'out_of_stock': return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-200'
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-200'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'in_stock': return <CheckCircleIcon className="h-4 w-4" />
      case 'low_stock': return <ExclamationTriangleIcon className="h-4 w-4" />
      case 'out_of_stock': return <ExclamationTriangleIcon className="h-4 w-4" />
      default: return null
    }
  }

  const loadInventoryCategories = async () => {
    try {
      const response = await apiService.getInventoryCategories()
      if (response.success && response.data && Array.isArray(response.data)) {
        setInventoryCategories((response.data as any[]).filter((cat: any) => cat.is_active))
      }
    } catch (error) {
      console.error('Error loading inventory categories:', error)
    }
  }

  // Use categories from settings, fallback to extracted categories
  const categories = inventoryCategories.length > 0 
    ? inventoryCategories.map(cat => cat.name)
    : [...new Set(inventory.map(item => item.category))]

  const handleAddItem = async () => {
    if (newItem.name && newItem.category && newItem.currentStock !== undefined && newItem.minStock !== undefined) {
      try {
        const itemData = {
          name: newItem.name,
          category: newItem.category,
          currentStock: newItem.currentStock || 0,
          minStock: newItem.minStock,
          unit: newItem.unit || 'pieces',
          costPerUnit: newItem.costPerUnit || 0,
          supplier: newItem.supplier || '',
          location: newItem.location || '',
          barcode: newItem.barcode || '',
          description: newItem.description || ''
        }
        
        const response = await apiService.createInventoryItem(itemData)
        if (response.success) {
          await loadInventory()
          setNewItem({})
          setShowAddModal(false)
          setToast({ message: 'Inventory item added successfully!', type: 'success' })
          setTimeout(() => setToast(null), 3000)
        } else {
          setToast({ message: response.error || 'Failed to add inventory item', type: 'error' })
          setTimeout(() => setToast(null), 3000)
        }
      } catch (error) {
        console.error('Error adding inventory item:', error)
        setToast({ message: 'Failed to add inventory item', type: 'error' })
        setTimeout(() => setToast(null), 3000)
      }
    }
  }

  const handleEditItem = (item: InventoryItem) => {
    setSelectedItem(item)
    // Populate form with item data
    setNewItem({
      name: item.name,
      category: item.category,
      currentStock: item.currentStock,
      minStock: item.minStock,
      unit: item.unit,
      costPerUnit: item.costPerUnit,
      supplier: item.supplier,
      location: item.location_id || item.location || '',
      barcode: item.barcode || '',
      description: item.description || ''
    })
    // Set supplier search term for the searchable dropdown
    if (item.supplier) {
      setSupplierSearchTerm(item.supplier)
    }
    setShowEditModal(true)
  }

  const handleSaveEdit = async () => {
    if (!selectedItem) return

    if (!newItem.name || String(newItem.name).trim() === '') {
      setToast({ message: 'Please enter item name', type: 'error' })
      setTimeout(() => setToast(null), 3000)
      return
    }

    try {
      const itemData = {
        name: newItem.name.trim(),
        category: newItem.category || '',
        currentStock: newItem.currentStock !== undefined && newItem.currentStock !== null ? Number(newItem.currentStock) : 0,
        minStock: newItem.minStock !== undefined && newItem.minStock !== null ? Number(newItem.minStock) : 0,
        unit: newItem.unit || 'pieces',
        costPerUnit: newItem.costPerUnit !== undefined && newItem.costPerUnit !== null ? Number(newItem.costPerUnit) : 0,
        supplier: newItem.supplier || '',
        location: newItem.location || '',
        barcode: newItem.barcode || '',
        description: newItem.description || ''
      }

      const response = await apiService.updateInventoryItem(selectedItem.id, itemData)
      if (response.success) {
        await loadInventory()
        setNewItem({})
        setSelectedItem(null)
        setShowEditModal(false)
        setSupplierSearchTerm('')
        setToast({ message: 'Inventory item updated successfully!', type: 'success' })
        setTimeout(() => setToast(null), 3000)
      } else {
        setToast({ message: response.error || 'Failed to update inventory item', type: 'error' })
        setTimeout(() => setToast(null), 3000)
      }
    } catch (error) {
      console.error('Error updating inventory item:', error)
      setToast({ message: 'Failed to update inventory item', type: 'error' })
      setTimeout(() => setToast(null), 3000)
    }
  }

  const handleDeleteItem = (item: InventoryItem) => {
    setItemToDelete(item)
    setShowDeleteModal(true)
  }

  const confirmDeleteItem = async () => {
    if (!itemToDelete) return
    
    try {
      const response = await apiService.deleteInventoryItem(itemToDelete.id)
      if (response.success) {
        await loadInventory()
        setShowDeleteModal(false)
        setItemToDelete(null)
        setToast({ message: 'Inventory item deleted successfully!', type: 'success' })
        setTimeout(() => setToast(null), 3000)
      } else {
        setToast({ message: response.error || 'Failed to delete inventory item', type: 'error' })
        setTimeout(() => setToast(null), 3000)
      }
    } catch (error) {
      console.error('Error deleting inventory item:', error)
      setToast({ message: 'Failed to delete inventory item', type: 'error' })
      setTimeout(() => setToast(null), 3000)
    }
  }

  const handleUpdateStock = (item: InventoryItem, type: 'add' | 'remove') => {
    setItemToUpdateStock(item)
    setStockUpdateType(type)
    setStockQuantity(0)
    setShowStockModal(true)
  }

  const confirmStockUpdate = async () => {
    if (!itemToUpdateStock || stockQuantity <= 0) {
      setToast({ message: 'Please enter a valid quantity greater than 0', type: 'error' })
      setTimeout(() => setToast(null), 3000)
      return
    }

    try {
      // Use new transaction system
      const transactionType = stockUpdateType === 'add' ? 'received' : 'issued'
      const response = await apiService.createStockTransaction({
        inventory_id: itemToUpdateStock.id,
        transaction_type: transactionType,
        quantity: stockQuantity,
        unit_price: itemToUpdateStock.costPerUnit || 0,
        notes: `Stock ${stockUpdateType === 'add' ? 'added' : 'removed'} via quick update`
      })

      if (response.success) {
        await loadInventory()
        setShowStockModal(false)
        setItemToUpdateStock(null)
        setStockQuantity(0)
        setToast({ message: `Stock ${stockUpdateType === 'add' ? 'added' : 'removed'} successfully!`, type: 'success' })
        setTimeout(() => setToast(null), 3000)
      } else {
        setToast({ message: response.error || 'Failed to update stock', type: 'error' })
        setTimeout(() => setToast(null), 3000)
      }
    } catch (error) {
      console.error('Error updating stock:', error)
      setToast({ message: 'Failed to update stock', type: 'error' })
      setTimeout(() => setToast(null), 3000)
    }
  }

  // Stock Transaction Functions
  const handleCreateTransaction = async () => {
    if (!transactionItem || transactionQuantity <= 0) {
      setToast({ message: 'Please select an item and enter a valid quantity', type: 'error' })
      setTimeout(() => setToast(null), 3000)
      return
    }

    setIsProcessingTransaction(true)
    try {
      const response = await apiService.createStockTransaction({
        inventory_id: transactionItem.id,
        transaction_type: transactionType,
        quantity: transactionQuantity,
        unit_price: transactionUnitPrice || transactionItem.costPerUnit || 0,
        reference_number: transactionReference || null,
        reference_type: transactionType === 'received' ? 'purchase' : transactionType === 'issued' ? 'usage' : 'adjustment',
        notes: transactionNotes || null,
        supplier_id: transactionSupplier || null
      })

      if (response.success) {
        await loadInventory()
        setShowTransactionModal(false)
        setTransactionItem(null)
        setTransactionQuantity(0)
        setTransactionUnitPrice(0)
        setTransactionReference('')
        setTransactionNotes('')
        setTransactionSupplier('')
        if (activeTab === 'ledger') {
          await loadStockLedger()
        }
        setToast({ message: 'Stock transaction recorded successfully!', type: 'success' })
        setTimeout(() => setToast(null), 3000)
      } else {
        setToast({ message: response.error || 'Failed to create transaction', type: 'error' })
        setTimeout(() => setToast(null), 3000)
      }
    } catch (error) {
      console.error('Error creating transaction:', error)
      setToast({ message: 'Failed to create transaction', type: 'error' })
      setTimeout(() => setToast(null), 3000)
    } finally {
      setIsProcessingTransaction(false)
    }
  }

  const loadStockLedger = async () => {
    setLoadingTransactions(true)
    try {
      const params: any = {}
      if (ledgerFilters.inventory_id) params.inventory_id = ledgerFilters.inventory_id
      if (ledgerFilters.transaction_type) params.transaction_type = ledgerFilters.transaction_type
      if (ledgerFilters.start_date) params.start_date = ledgerFilters.start_date
      if (ledgerFilters.end_date) params.end_date = ledgerFilters.end_date

      const response = await apiService.getStockTransactions(params)
      if (response.success) {
        setStockTransactions(Array.isArray(response.data) ? response.data : [])
      } else {
        console.error('Failed to load stock ledger:', response.error)
      }
    } catch (error) {
      console.error('Error loading stock ledger:', error)
    } finally {
      setLoadingTransactions(false)
    }
  }

  const loadStockReports = async () => {
    if (!reportFilters.start_date || !reportFilters.end_date) {
      return
    }

    setLoadingReports(true)
    try {
      const params: any = {
        start_date: reportFilters.start_date,
        end_date: reportFilters.end_date,
        group_by: reportFilters.group_by
      }
      if (reportFilters.inventory_id) params.inventory_id = reportFilters.inventory_id

      const response = await apiService.getStockReports(params)
      if (response.success && response.data) {
        setStockReports(response.data)
      } else {
        console.error('Failed to load stock reports:', response.error || 'Unknown error')
        setToast({ message: response.error || 'Failed to load stock reports', type: 'error' })
      }
    } catch (error: any) {
      console.error('Error loading stock reports:', error)
      setToast({ message: error.message || 'Failed to load stock reports', type: 'error' })
    } finally {
      setLoadingReports(false)
    }
  }

  // const updateStock = async (itemId: string, newStock: number) => {
  //   try {
  //     const response = await apiService.updateInventoryStock(itemId, newStock)
  //     if (response.success) {
  //       await loadInventory()
  //     } else {
  //       alert(response.error || 'Failed to update stock')
  //     }
  //   } catch (error) {
  //     console.error('Error updating stock:', error)
  //     alert('Failed to update stock')
  //   }
  // }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate">
            Inventory Management
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Track stock levels, manage suppliers, and monitor inventory
          </p>
        </div>
        <div className="mt-4 flex gap-2 md:mt-0 md:ml-4">
          {activeTab === 'items' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Add Item
            </button>
          )}
          {activeTab === 'transactions' && (
            <button
              onClick={() => {
                setTransactionItem(null)
                setTransactionType('received')
                setTransactionQuantity(0)
                setTransactionUnitPrice(0)
                setTransactionReference('')
                setTransactionNotes('')
                setTransactionSupplier('')
                setShowTransactionModal(true)
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              New Transaction
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('items')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'items'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Items
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'transactions'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Stock Transactions
          </button>
          <button
            onClick={() => {
              setActiveTab('ledger')
              loadStockLedger()
            }}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'ledger'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Stock Ledger
          </button>
          <button
            onClick={() => {
              setActiveTab('reports')
              loadStockReports()
            }}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'reports'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Stock Reports
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'items' && (
        <>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    In Stock
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {inventory.filter(item => item.status === 'in_stock').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Low Stock
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {inventory.filter(item => item.status === 'low_stock').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Out of Stock
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {inventory.filter(item => item.status === 'out_of_stock').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FunnelIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Total Items
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {inventory.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="in_stock">In Stock</option>
            <option value="low_stock">Low Stock</option>
            <option value="out_of_stock">Out of Stock</option>
          </select>

          {/* Sort */}
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-')
              setSortBy(field)
              setSortOrder(order as 'asc' | 'desc')
            }}
            className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <option value="currentStock-desc">Stock High-Low</option>
            <option value="currentStock-asc">Stock Low-High</option>
            <option value="lastUpdated-desc">Recently Updated</option>
            <option value="costPerUnit-desc">Cost High-Low</option>
          </select>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Item
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredInventory.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {item.location}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {item.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {item.currentStock} {item.unit}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Alert Threshold: {item.minStock}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {getStatusIcon(item.status)}
                      <span className="ml-1 capitalize">{item.status.replace('_', ' ')}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {formatCurrency(item.costPerUnit)}/{item.unit}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {item.supplier}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {item.lastUpdated.toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleUpdateStock(item, 'add')}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                        title="Add Stock"
                      >
                        <ArrowUpIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleUpdateStock(item, 'remove')}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Remove Stock"
                      >
                        <ArrowDownIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditItem(item)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}

      {/* Stock Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Quick Stock Actions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {inventory.map((item) => (
                <div key={item.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900 dark:text-white">{item.name}</h4>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {item.currentStock} {item.unit}
                    </span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => {
                        setTransactionItem(item)
                        setTransactionType('received')
                        setTransactionQuantity(0)
                        setTransactionUnitPrice(item.costPerUnit || 0)
                        setTransactionReference('')
                        setTransactionNotes('')
                        setTransactionSupplier('')
                        setShowTransactionModal(true)
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-3 rounded transition-colors"
                    >
                      Receive
                    </button>
                    <button
                      onClick={() => {
                        setTransactionItem(item)
                        setTransactionType('issued')
                        setTransactionQuantity(0)
                        setTransactionUnitPrice(item.costPerUnit || 0)
                        setTransactionReference('')
                        setTransactionNotes('')
                        setTransactionSupplier('')
                        setShowTransactionModal(true)
                      }}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-3 rounded transition-colors"
                    >
                      Issue
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stock Ledger Tab */}
      {activeTab === 'ledger' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Stock Ledger Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <select
                value={ledgerFilters.inventory_id}
                onChange={(e) => {
                  setLedgerFilters({ ...ledgerFilters, inventory_id: e.target.value })
                }}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Items</option>
                {inventory.map(item => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>

              <select
                value={ledgerFilters.transaction_type}
                onChange={(e) => {
                  setLedgerFilters({ ...ledgerFilters, transaction_type: e.target.value })
                }}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Types</option>
                <option value="received">Received</option>
                <option value="issued">Issued</option>
                <option value="adjusted">Adjusted</option>
                <option value="returned">Returned</option>
                <option value="wastage">Wastage</option>
              </select>

              <input
                type="date"
                value={ledgerFilters.start_date}
                onChange={(e) => {
                  setLedgerFilters({ ...ledgerFilters, start_date: e.target.value })
                }}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Start Date"
              />

              <div className="flex gap-2">
                <input
                  type="date"
                  value={ledgerFilters.end_date}
                  onChange={(e) => {
                    setLedgerFilters({ ...ledgerFilters, end_date: e.target.value })
                  }}
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="End Date"
                />
                <button
                  onClick={loadStockLedger}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                >
                  <ArrowPathIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Ledger Table */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            {loadingTransactions ? (
              <div className="text-center py-12">
                <ArrowPathIcon className="h-8 w-8 text-gray-400 mx-auto mb-2 animate-spin" />
                <p className="text-gray-500 dark:text-gray-400">Loading transactions...</p>
              </div>
            ) : stockTransactions.length === 0 ? (
              <div className="text-center py-12">
                <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 dark:text-gray-400">No transactions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Item</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Quantity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Unit Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Total Cost</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Balance Before</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Balance After</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Reference</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {stockTransactions.map((trans) => (
                      <tr key={trans.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {new Date(trans.created_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {trans.inventory?.name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            trans.transaction_type === 'received' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                            trans.transaction_type === 'issued' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                            trans.transaction_type === 'adjusted' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                            trans.transaction_type === 'returned' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                          }`}>
                            {trans.transaction_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {trans.quantity} {trans.inventory?.unit || ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatCurrency(trans.unit_price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatCurrency(trans.total_cost)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {trans.balance_before} {trans.inventory?.unit || ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {trans.balance_after} {trans.inventory?.unit || ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {trans.reference_number || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {trans.notes || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stock Reports Tab */}
      {activeTab === 'reports' && (
        <div className="space-y-6">
          {/* Report Filters */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Stock Reports
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <select
                value={reportFilters.inventory_id}
                onChange={(e) => {
                  setReportFilters({ ...reportFilters, inventory_id: e.target.value })
                }}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Items</option>
                {inventory.map(item => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>

              <input
                type="date"
                value={reportFilters.start_date}
                onChange={(e) => {
                  setReportFilters({ ...reportFilters, start_date: e.target.value })
                }}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />

              <input
                type="date"
                value={reportFilters.end_date}
                onChange={(e) => {
                  setReportFilters({ ...reportFilters, end_date: e.target.value })
                }}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />

              <div className="flex gap-2">
                <select
                  value={reportFilters.group_by}
                  onChange={(e) => {
                    setReportFilters({ ...reportFilters, group_by: e.target.value as 'day' | 'week' | 'month' })
                  }}
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="day">By Day</option>
                  <option value="week">By Week</option>
                  <option value="month">By Month</option>
                </select>
                <button
                  onClick={loadStockReports}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                >
                  Generate
                </button>
              </div>
            </div>
          </div>

          {/* Reports Content */}
          {loadingReports ? (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
              <ArrowPathIcon className="h-8 w-8 text-gray-400 mx-auto mb-2 animate-spin" />
              <p className="text-gray-500 dark:text-gray-400">Generating reports...</p>
            </div>
          ) : stockReports ? (
            <div className="space-y-6">
              {/* Show message if no data */}
              {(!stockReports.period_summary || stockReports.period_summary.length === 0) && 
               (!stockReports.item_summary || stockReports.item_summary.length === 0) ? (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
                  <p className="text-gray-500 dark:text-gray-400">
                    No transactions found for the selected date range.
                  </p>
                </div>
              ) : (
                <>
              {/* Period Summary */}
              {stockReports.period_summary && stockReports.period_summary.length > 0 && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Period Summary ({reportFilters.group_by})
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Period</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Received</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Issued</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Adjusted</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Returned</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Wastage</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Received Cost</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Issued Cost</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {stockReports.period_summary.map((period: any, idx: number) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {period.period}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400">
                              {(parseFloat(period.received) || 0).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">
                              {(parseFloat(period.issued) || 0).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 dark:text-blue-400">
                              {(parseFloat(period.adjusted) || 0).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 dark:text-yellow-400">
                              {(parseFloat(period.returned) || 0).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                              {(parseFloat(period.wastage) || 0).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {formatCurrency(period.total_received_cost)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {formatCurrency(period.total_issued_cost)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Item Summary */}
              {stockReports.item_summary && stockReports.item_summary.length > 0 && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Item Summary
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Item</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Total Received</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Receive Count</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Total Issued</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Issue Count</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Current Balance</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Received Cost</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Issued Cost</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {stockReports.item_summary.map((item: any, idx: number) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {item.item_name || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {item.category || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400">
                              {(parseFloat(item.total_received) || 0).toFixed(2)} {item.unit || ''}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {item.receive_count}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">
                              {(parseFloat(item.total_issued) || 0).toFixed(2)} {item.unit || ''}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {item.issue_count}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {(parseFloat(item.current_balance) || 0).toFixed(2)} {item.unit || ''}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {formatCurrency(item.total_received_cost)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {formatCurrency(item.total_issued_cost)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
                </>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
              <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500 dark:text-gray-400">Select date range and click Generate to view reports</p>
            </div>
          )}
        </div>
      )}

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add New Item
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Item Name
                </label>
                <input
                  type="text"
                  value={newItem.name || ''}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter item name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <select
                    value={newItem.category || ''}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Manage categories in Settings → Inventory
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Unit
                  </label>
                  <input
                    type="text"
                    value={newItem.unit || ''}
                    onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="kg, pcs, etc."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Stock
                  </label>
                  <input
                    type="number"
                    value={newItem.currentStock || ''}
                    onChange={(e) => setNewItem({ ...newItem, currentStock: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Current stock level
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Alert Threshold
                  </label>
                  <input
                    type="number"
                    value={newItem.minStock || ''}
                    onChange={(e) => setNewItem({ ...newItem, minStock: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Alert when stock falls below this level
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cost Per Unit
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newItem.costPerUnit || ''}
                  onChange={(e) => setNewItem({ ...newItem, costPerUnit: Number(e.target.value) })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="0.00"
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Supplier
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={supplierSearchTerm || newItem.supplier || ''}
                    onChange={(e) => {
                      const value = e.target.value
                      setSupplierSearchTerm(value)
                      setShowSupplierDropdown(true)
                      setNewItem({ ...newItem, supplier: value })
                    }}
                    onFocus={() => {
                      setShowSupplierDropdown(true)
                      if (!supplierSearchTerm && newItem.supplier) {
                        setSupplierSearchTerm(newItem.supplier)
                      }
                    }}
                    onBlur={() => {
                      // Delay to allow click on dropdown item
                      setTimeout(() => {
                        setShowSupplierDropdown(false)
                        // If supplier is set but search term doesn't match, keep supplier
                        if (newItem.supplier && !supplierSearchTerm) {
                          setSupplierSearchTerm(newItem.supplier)
                        }
                      }, 200)
                    }}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Search or select supplier"
                  />
                  {showSupplierDropdown && filteredSuppliers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredSuppliers.map((vendor) => (
                        <div
                          key={vendor.id}
                          onClick={() => {
                            const vendorName = vendor.name || vendor.vendor_name
                            setNewItem({ ...newItem, supplier: vendorName })
                            setSupplierSearchTerm(vendorName)
                            setShowSupplierDropdown(false)
                          }}
                          className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-white"
                        >
                          {vendor.name || vendor.vendor_name}
                        </div>
                      ))}
                    </div>
                  )}
                  {showSupplierDropdown && filteredSuppliers.length === 0 && supplierSearchTerm.trim() !== '' && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg">
                      <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                        No suppliers found
                      </div>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Manage vendors in Accounting → Vendors
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Location
                </label>
                <select
                  value={newItem.location || ''}
                  onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select a location</option>
                  {locations.filter(loc => loc.is_active).map((location) => (
                    <option key={location.id} value={location.id}>
                      {getLocationPath(location.id)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Manage locations in Settings → Inventory
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddItem}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Add Item
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Edit Inventory Item
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Item Name *
                </label>
                <input
                  type="text"
                  value={newItem.name || ''}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter item name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <select
                    value={newItem.category || ''}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Manage categories in Settings → Inventory
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Unit
                  </label>
                  <input
                    type="text"
                    value={newItem.unit || ''}
                    onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="kg, pcs, etc."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Stock
                  </label>
                  <input
                    type="number"
                    value={newItem.currentStock || ''}
                    onChange={(e) => setNewItem({ ...newItem, currentStock: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Current stock level
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Alert Threshold
                  </label>
                  <input
                    type="number"
                    value={newItem.minStock || ''}
                    onChange={(e) => setNewItem({ ...newItem, minStock: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Alert when stock falls below this level
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cost Per Unit
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newItem.costPerUnit || ''}
                  onChange={(e) => setNewItem({ ...newItem, costPerUnit: Number(e.target.value) })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="0.00"
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Supplier
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={supplierSearchTerm || newItem.supplier || ''}
                    onChange={(e) => {
                      const value = e.target.value
                      setSupplierSearchTerm(value)
                      setShowSupplierDropdown(true)
                      setNewItem({ ...newItem, supplier: value })
                    }}
                    onFocus={() => {
                      setShowSupplierDropdown(true)
                      if (!supplierSearchTerm && newItem.supplier) {
                        setSupplierSearchTerm(newItem.supplier)
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setShowSupplierDropdown(false)
                        if (newItem.supplier && !supplierSearchTerm) {
                          setSupplierSearchTerm(newItem.supplier)
                        }
                      }, 200)
                    }}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Search or select supplier"
                  />
                  {showSupplierDropdown && filteredSuppliers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredSuppliers.map((vendor) => (
                        <div
                          key={vendor.id}
                          onClick={() => {
                            const vendorName = vendor.name || vendor.vendor_name
                            setNewItem({ ...newItem, supplier: vendorName })
                            setSupplierSearchTerm(vendorName)
                            setShowSupplierDropdown(false)
                          }}
                          className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-white"
                        >
                          {vendor.name || vendor.vendor_name}
                        </div>
                      ))}
                    </div>
                  )}
                  {showSupplierDropdown && filteredSuppliers.length === 0 && supplierSearchTerm.trim() !== '' && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg">
                      <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                        No suppliers found
                      </div>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Manage vendors in Accounting → Vendors
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Location
                </label>
                <select
                  value={newItem.location || ''}
                  onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select a location</option>
                  {locations.filter(loc => loc.is_active).map((location) => (
                    <option key={location.id} value={location.id}>
                      {getLocationPath(location.id)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Manage locations in Settings → Inventory
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowEditModal(false)
                    setSelectedItem(null)
                    setNewItem({})
                    setSupplierSearchTerm('')
                  }}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Update Item
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Inventory Item
            </h3>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Are you sure you want to delete this item?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <p className="font-medium text-gray-900 dark:text-white">{itemToDelete.name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Category: {itemToDelete.category} | Stock: {itemToDelete.currentStock} {itemToDelete.unit}
                </p>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-3">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setItemToDelete(null)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteItem}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Delete Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Update Modal */}
      {showStockModal && itemToUpdateStock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {stockUpdateType === 'add' ? 'Add Stock' : 'Remove Stock'}
            </h3>
            
            <div className="mb-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
                <p className="font-medium text-gray-900 dark:text-white">{itemToUpdateStock.name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Current Stock: {itemToUpdateStock.currentStock} {itemToUpdateStock.unit}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Quantity to {stockUpdateType === 'add' ? 'Add' : 'Remove'} *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={stockQuantity || ''}
                  onChange={(e) => setStockQuantity(Number(e.target.value))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter quantity"
                  autoFocus
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {stockUpdateType === 'add' 
                    ? `New stock will be: ${itemToUpdateStock.currentStock + (stockQuantity || 0)} ${itemToUpdateStock.unit}`
                    : `New stock will be: ${Math.max(0, itemToUpdateStock.currentStock - (stockQuantity || 0))} ${itemToUpdateStock.unit}`
                  }
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowStockModal(false)
                  setItemToUpdateStock(null)
                  setStockQuantity(0)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmStockUpdate}
                className={`flex-1 font-medium py-2 px-4 rounded-lg transition-colors ${
                  stockUpdateType === 'add'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                {stockUpdateType === 'add' ? 'Add Stock' : 'Remove Stock'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Transaction Modal */}
      {showTransactionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {transactionType === 'received' ? 'Receive Stock' :
                 transactionType === 'issued' ? 'Issue Stock' :
                 transactionType === 'adjusted' ? 'Adjust Stock' :
                 transactionType === 'returned' ? 'Return Stock' :
                 'Record Wastage'}
              </h3>
              <button
                onClick={() => {
                  setShowTransactionModal(false)
                  setTransactionItem(null)
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Item *
                </label>
                <select
                  value={transactionItem?.id || ''}
                  onChange={(e) => {
                    const item = inventory.find(i => i.id === e.target.value)
                    setTransactionItem(item || null)
                    if (item) {
                      setTransactionUnitPrice(item.costPerUnit || 0)
                    }
                  }}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select Item</option>
                  {inventory.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name} (Current: {item.currentStock} {item.unit})
                    </option>
                  ))}
                </select>
              </div>

              {transactionItem && (
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Current Stock: <span className="font-medium text-gray-900 dark:text-white">{transactionItem.currentStock} {transactionItem.unit}</span>
                  </p>
                  {transactionType === 'received' && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      New Stock: <span className="font-medium text-green-600 dark:text-green-400">
                        {transactionItem.currentStock + (transactionQuantity || 0)} {transactionItem.unit}
                      </span>
                    </p>
                  )}
                  {transactionType === 'issued' && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      New Stock: <span className="font-medium text-red-600 dark:text-red-400">
                        {Math.max(0, transactionItem.currentStock - (transactionQuantity || 0))} {transactionItem.unit}
                      </span>
                    </p>
                  )}
                  {transactionType === 'adjusted' && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      New Stock: <span className="font-medium text-blue-600 dark:text-blue-400">
                        {transactionQuantity || 0} {transactionItem.unit}
                      </span>
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Transaction Type *
                </label>
                <select
                  value={transactionType}
                  onChange={(e) => setTransactionType(e.target.value as any)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="received">Received</option>
                  <option value="issued">Issued</option>
                  <option value="adjusted">Adjusted</option>
                  <option value="returned">Returned</option>
                  <option value="wastage">Wastage</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={transactionQuantity || ''}
                    onChange={(e) => setTransactionQuantity(parseFloat(e.target.value) || 0)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Unit Price
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={transactionUnitPrice || ''}
                    onChange={(e) => setTransactionUnitPrice(parseFloat(e.target.value) || 0)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {transactionType === 'received' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Supplier
                  </label>
                  <select
                    value={transactionSupplier}
                    onChange={(e) => setTransactionSupplier(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select Supplier</option>
                    {vendors.map(vendor => (
                      <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reference Number
                </label>
                <input
                  type="text"
                  value={transactionReference}
                  onChange={(e) => setTransactionReference(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Invoice #, GRN #, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes
                </label>
                <textarea
                  value={transactionNotes}
                  onChange={(e) => setTransactionNotes(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Additional notes..."
                />
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => {
                  setShowTransactionModal(false)
                  setTransactionItem(null)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTransaction}
                disabled={isProcessingTransaction || !transactionItem || !transactionQuantity}
                className={`flex-1 font-medium py-2 px-4 rounded-lg transition-colors ${
                  transactionType === 'received' || transactionType === 'returned'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : transactionType === 'issued' || transactionType === 'wastage'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                } disabled:bg-gray-400 disabled:cursor-not-allowed`}
              >
                {isProcessingTransaction ? 'Processing...' : 'Record Transaction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-[9999]">
          <div className={`w-80 bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden ${
            toast.type === 'success' ? 'border-l-4 border-green-500' : 'border-l-4 border-red-500'
          }`}>
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {toast.type === 'success' ? (
                    <CheckIcon className="h-6 w-6 text-green-500" />
                  ) : (
                    <XMarkIcon className="h-6 w-6 text-red-500" />
                  )}
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className={`text-base font-medium ${
                    toast.type === 'success' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
                  }`}>
                    {toast.message}
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0 flex">
                  <button
                    className="bg-white dark:bg-gray-800 rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => setToast(null)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Inventory
